import classNames from 'classnames'
import { type SetStateAction, type Dispatch, type ChangeEvent } from 'react'
import { v4 } from 'uuid'

import { EIcons } from '@/app/types/iconType'
import { BlurApp, Button, Icon } from '@/shared'

import { type IICons, type IAdditionalInfo2Editable, type TIconTypes } from '../../helper'

import { ContentItem } from './ContentItem/ContentItem'
import style from './SectionItem.module.scss'

interface IProps extends IAdditionalInfo2Editable {
    setData: Dispatch<SetStateAction<IAdditionalInfo2Editable[]>>
    onUpdateAdditionalInfo: () => void
    onGetAdditionalInfoActualData: (onSuccess?: () => void) => void
    onRemoveAdditionalInfo: (sectionId: string) => void
}

export const SectionItem = (props: IProps) => {
    const {
        id,
        title,
        items,
        itemType,
        setData,
        onUpdateAdditionalInfo,
        onRemoveAdditionalInfo,
        onGetAdditionalInfoActualData
    } = props

    const onSaveSection = () => {
        onUpdateAdditionalInfo()
    }

    const onSetSectionToUpdate = () => {
        setData((prev) => prev.map((item) => (item.id === id ? { ...item, itemType: 'update' } : item)))
    }

    const onRemoveSection = () => {
        onRemoveAdditionalInfo(id)
    }

    const onCancelCreatingSection = () => {
        setData((prev) => prev.filter((item) => item.id !== id))
    }

    const onCancelUpdatingSection = () => {
        onGetAdditionalInfoActualData(() =>
            setData((prev) => prev.map((item) => (item.id === id ? { ...item, itemType: 'read' } : item)))
        )
    }

    const onChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget
        setData((prev) => prev.map((item) => (item.id === id ? { ...item, title: value } : item)))
    }

    const actions: IICons[] = [
        {
            type: 'edit',
            callback: onSetSectionToUpdate,
            disbled: false,
            belongTo: 'read'
        },
        {
            type: 'remove',
            callback: onRemoveSection,
            disbled: false,
            belongTo: 'read'
        },
        {
            type: 'save',
            callback: onSaveSection,
            disbled: title.length < 1 || items.length < 1 || items.some((el) => el.title.length < 1),
            belongTo: 'create',
            iconColor: '#007dee'
        },
        {
            type: 'cancel',
            callback: onCancelCreatingSection,
            disbled: false,
            belongTo: 'create',
            iconColor: 'black'
        },
        {
            type: 'save',
            callback: onSaveSection,
            disbled: title.length < 1 || items.length < 1 || items.some((el) => el.title.length < 1),
            belongTo: 'update',
            iconColor: '#007dee'
        },
        {
            type: 'cancel',
            callback: onCancelUpdatingSection,
            disbled: false,
            belongTo: 'update',
            iconColor: 'black'
        }
    ]

    const ICONS_MAPPER: Record<TIconTypes, EIcons> = {
        add: EIcons.PLUS,
        save: EIcons.PLUS,
        edit: EIcons.EDIT,
        remove: EIcons.TRSH,
        cancel: EIcons.CLOS
    }

    const onAddChild = () => {
        setData((prev) =>
            prev.map((section) =>
                section.id === id ? { ...section, items: [...section.items, { title: '', id: v4() }] } : section
            )
        )
    }

    const onRemoveChildFromList = (childId: string) => {
        setData((prev) =>
            prev.map((section) =>
                section.id === id ? { ...section, items: section.items.filter((item) => item.id !== childId) } : section
            )
        )
    }

    return (
        <div className={classNames(style.container, itemType !== 'read' && style.containerBlured)}>
            <div className={style.sectionWrapper}>
                <BlurApp show={itemType !== 'read'} withScroll />
                {itemType === 'read' ? (
                    <span>{title}</span>
                ) : (
                    <input value={title} autoFocus onChange={onChangeTitle} placeholder='Наименование описания' />
                )}
                <div className={style.icons}>
                    {actions
                        .filter(({ belongTo }) => belongTo === itemType)
                        .map((action, idx) => (
                            <div
                                key={idx}
                                className={classNames(style.icon, action.disbled && style.iconDisabled)}
                                onClick={action.callback}
                            >
                                <Icon iconName={ICONS_MAPPER[action.type]} color={action.iconColor} />
                            </div>
                        ))}
                </div>
            </div>
            {items.length < 1 && (
                <div className={style.emptyContent}>
                    <span>Описания без содержания не отобразятся на ЛК</span>
                    {itemType !== 'read' && <Button onClick={onAddChild}>Добавить первое содержание</Button>}
                </div>
            )}
            <div className={style.children}>
                {items.length > 0 &&
                    items.map((item) => (
                        <ContentItem
                            key={item.id}
                            itemType={itemType}
                            iconsMapper={ICONS_MAPPER}
                            onRemoveChild={onRemoveChildFromList}
                            parentId={id}
                            setData={setData}
                            {...item}
                        />
                    ))}
            </div>
            {items.length > 0 && itemType !== 'read' && (
                <div className={style.addChild}>
                    <Button onClick={onAddChild} disabled={items.some((el) => el.title.length < 1)}>
                        {`Добавить содержание`}
                    </Button>
                </div>
            )}
        </div>
    )
}
